import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { $dashboardMerchantsStore, $initialCurrenciesAvailableStore } from '@lib/store';
import { useCurrencySymbol } from '@shared/hooks';
import { AmountUtils, ColorUtils, DateUtils, PaletteUtils } from '@shared/utils';
import { Select, Text } from '@shared/widgets';

const dates = DateUtils.getLastMonths();

export const DashboardMerchants = () => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const dashboardMerchantsStore = useUnit($dashboardMerchantsStore.store);

  const [currency, setCurrency] = useState<string>(currenciesAvailableStore.data[0]);
  const [date, setDate] = useState<string>(dates[0].value);

  useEffect(() => {
    if (currency) {
      $dashboardMerchantsStore.events.update({ currency, date });
    }
  }, [currency, date]);

  const symbol = useCurrencySymbol(currency);

  const colors = useMemo(() => {
    if (PaletteUtils.isVcfcPalette) return PaletteUtils.expenseCategoriesVcfcColors;
    return dashboardMerchantsStore.data.map(() => ColorUtils.getRandomColor());
  }, [dashboardMerchantsStore]);
  const options = useMemo<ApexOptions>(() => {
    return {
      chart: {
        id: 'bar',
        width: '100%',
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        toolbar: {
          show: false,
        },
      },
      noData: {
        text: 'No data',
        style: {
          color: 'var(--text-slate)',
        },
      },
      colors: colors,
      stroke: {
        width: [0, 10, 10],
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ['var(--text-slate)'],
        },
        formatter: function (val: string) {
          return (
            symbol +
            parseInt(val)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          );
        },
        offsetX: 20,
        offsetY: 0,
        dropShadow: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '100%',
          barHeight: '60%',
          distributed: true,
          rangeBarOverlap: true,
          rangeBarGroupRows: false,
          horizontal: true,
          dataLabels: {
            position: 'bottom',
          },
        },
      },
      xaxis: {
        tickAmount: 6,
        labels: {
          style: {
            colors: ['var(--text-slate)'],
          },
          show: true,
          formatter: function (value: string) {
            const replaceValue = value
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              .split(',');
            const replaceArray = replaceValue
              .map((item, i) => {
                if ((i === replaceValue.length - 1 && value < '1000000') || (value >= '1000000' && i !== 0)) {
                  return item.replaceAll('0', '');
                }
                return item;
              })
              .filter((x) => x !== '');
            return value == '0' || value < '1000' ? value : value >= '1000000' ? replaceArray + 'm' : replaceArray + 'k';
          },
        },
        categories: dashboardMerchantsStore.data.map((m) => m.merchant || ''),
      },
      yaxis: {
        tickAmount: 4,
        labels: {
          show: true,
          style: {
            colors: ['var(--text-slate)'], // #bebfc0
            fontSize: '15px',
            fontFamily: '"Proxima Nova", sans-serif',
            fontWeight: 500,
          },
          offsetX: -10,
          offsetY: 0,
        },
        axisTicks: {
          show: false,
        },
        tooltip: {
          enabled: false,
          offsetX: 0,
        },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        x: {
          show: true,
          format: 'dd MMM yyyy',
          formatter: function (value) {
            return ' ' + value;
          },
        },
        y: {
          formatter: function (value) {
            return symbol + AmountUtils.transformToAmountWithCurrency(value);
          },
        },
      },
      legend: {
        show: false,
      },
      grid: {
        show: false,
      },
    };
  }, [dashboardMerchantsStore, symbol, colors]);
  const series = useMemo(() => [{ name: '', data: dashboardMerchantsStore.data.map((ex) => ex.amount) }], [dashboardMerchantsStore]);

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex justify-between'>
        <Text weight='medium' size='2xl'>
          Most used merchants
        </Text>
        <div className='flex gap-4'>
          <Select className='min-w-[100px]' items={currenciesAvailableStore.data} value={currency} onChange={(value) => setCurrency(value!)} />
          <Select
            wrapperClassName='min-w-40'
            items={dates.map((d) => ({ value: d.value, label: `${d.name} ${new Date(d.value).getFullYear()}` }))}
            value={date}
            onChange={(val) => setDate(val!)}
          />
        </div>
      </div>
      <Chart options={options} series={series} type='bar' height={400} />
      <Text className='text-right -mt-5'>Spending maximum</Text>
    </div>
  );
};
